import classNames from 'classnames';
import { useEffect, useState } from 'react';
import * as React from 'react';

export type RenderActiveTab = (i: number) => void;

export interface IProps {
  headers: Array<React.ReactElement<any>>;
  renderActiveTab: RenderActiveTab;
  getUniqueHeaderClass?: (idx: number) => string;
  headerClasses?: string;
  activeTabClasses?: string;
  initialTab?: number;
}

/**
 * Used to display tabs, within tiles @see {tiles.jsx}, doesn't add any styling, only adds tab functionality.
 * The active tab is passed to the renderActiveTab function so the parent can decide how to render the content
 * @param headers An array of elements to render as the tab headers
 * @param headerClasses (Optional) A string containing classes to be added to the header tabs
 * @param activeTabClasses (Optional) A string containing classes to be added to the ACTIVE header
 * @param renderActiveTab A function which will be called when a tab is selected which is passed the activeTab header index
 * @param initialTab (Optional) A number which will determine the initially selected tab (Defaults to 0)
 */

const TabLogic = ({
  headers,
  headerClasses,
  activeTabClasses,
  initialTab,
  renderActiveTab,
  getUniqueHeaderClass,
}: IProps) => {
  const [activeIndex, setActiveIndex] = useState((initialTab || 0) >= headers.length ? 0 : initialTab || 0);

  useEffect(() => {
    renderActiveTab(activeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  return (
    <>
      {headers.map((header, idx) => {
        const isActive = idx === activeIndex;
        const uniqueClasses = getUniqueHeaderClass !== undefined ? getUniqueHeaderClass(idx) : '';

        return (
          // Could add the ability to pass in different elements to render, EG an li
          <div
            className={classNames(headerClasses, uniqueClasses, {
              [`${activeTabClasses || 'is-active'}`]: isActive,
            })}
            id={`tab-${idx}`}
            key={idx}
            onClick={() => setActiveIndex(idx)}
          >
            {header}
          </div>
        );
      })}
    </>
  );
};

export { TabLogic as Tabs };
